import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import get from 'lodash/get';

import {
  ButtonGroup,
  Modal,
  ModalHeader,
  Container,
  Row,
  Col,
} from 'reactstrap';
import Input from 'components/Form/Input';
import CardNumberInput from '@lendingusa/react-common-card-number-input';
import { Button } from 'components/Button';
import Select from 'components/Form/Select';
import Validator from 'components/Validator/Validator';
import { states } from 'utils/states';
import { numberMask, numberUnmask, removeSpace, removeUnderline } from 'utils/masks';
import VerifiedIcon from 'assets/images/correct.png';
import WarningIcon from 'assets/images/warning.png';
import { buildPostPayload, buildPutPayload } from 'utils/paymentAccountMapper';
import schema from './addPaymentMethodSchema';
import debitCardSchema from './addDebitCardSchema';
import editBankAccountSchema from './editBankAccountSchema';
import './style.scss';

const INITIAL_VALIDATION_STATE = {
  loading: false,
  verified: false,
  checkedFirstTime: false,
  data: null,
};

const ROUTING_NUMBER_HELP = (
  <>
    <strong>Where to find it</strong>
    In your banking app, open the account and look under account details. On a paper check, it&apos;s the left-most 9-digit number along the bottom.
  </>
);

const getBankInitials = (bankName = '') => {
  const words = `${bankName}`.replace(/[.,]/g, ' ').split(/\s+/).filter(Boolean);
  const skip = new Set(['bank', 'n', 'a', 'na', 'the', 'of', 'and']);
  const significant = words.filter((word) => !skip.has(word.toLowerCase()));
  const source = (significant.length >= 2 ? significant : words).slice(0, 2);
  return source.map((word) => word[0]).join('').toUpperCase() || 'BA';
};

const getProfileAddress = () => {
  try {
    const customerDetail = JSON.parse(sessionStorage.getItem('customerDetail'));
    return get(customerDetail, 'primaryAddress') || get(customerDetail, 'mailAddress') || null;
  } catch (err) {
    return null;
  }
};

const AddPaymentMethods = (props) => {
  const { validator: { values, errors, validate, setValues, setErrors, deleteErrors, resetErrors }, paymentModalType, currentPaymentMethod, validateBankRoutingNumber } = props;
  const [activeTab, setActiveTab] = useState(0);
  const [allowExpirationDate, setAllowExpirationDate] = useState(true);
  const [isAddingPayment, setIsAddingPayment] = useState(false);
  const [routingValidateStatus, setRoutingValidateStatus] = useState(INITIAL_VALIDATION_STATE);
  const [isAccountNumberVisible, setIsAccountNumberVisible] = useState(true);
  const [sameAsProfileAddress, setSameAsProfileAddress] = useState(null);
  const [cardError, setCardError] = useState('');
  const accountNumberFieldRef = useRef(null);

  const applyProfileAddress = () => {
    const nextProfileAddress = getProfileAddress();
    if (!nextProfileAddress) return false;

    const rawState = nextProfileAddress.state || '';
    const matchedState = states.find(
      (item) => item.value === rawState || `${item.title}`.toLowerCase() === `${rawState}`.toLowerCase()
    );

    setValues({
      ...values,
      address: nextProfileAddress.address1 || nextProfileAddress.address || '',
      city: nextProfileAddress.city || '',
      state: matchedState ? matchedState.value : rawState,
      zipcode: `${nextProfileAddress.zipcode || nextProfileAddress.zipCode || ''}`.slice(0, 5),
    });
    deleteErrors(['address', 'city', 'state', 'zipcode']);
    return true;
  };

  const clearAddressFields = () => {
    setValues({
      ...values,
      address: '',
      city: '',
      state: '',
      zipcode: '',
    });
  };

  const resetFormState = () => {
    setActiveTab(0);
    setRoutingValidateStatus(INITIAL_VALIDATION_STATE);
    setIsAccountNumberVisible(true);
    setSameAsProfileAddress(null);
    setValues({});
    resetErrors();
  };

  const validateRoutingNo = (routingNumber) => {
    const newValue = routingNumber.replace(/[^\d]/g, '');
    if (newValue.length === 9) {
      return true;
    }
    return false;
  };

  const validateRoutingNumber = (routingNo) => {
    let routingNumberUnMask;
    if (routingNo && routingNo.length === 9) {
      routingNumberUnMask = routingNo.replace(/[^\d]/g, '');
    }
    if (!routingNumberUnMask && routingNumberUnMask?.length !== 9) return;
    setRoutingValidateStatus({
      ...routingValidateStatus,
      checkedFirstTime: true,
      loading: true,
      verified: false,
      data: null,
    });
    setValues({ ...values, bankName: '', isValidRoutingNumber: false });
    if (routingNo) {
      validateBankRoutingNumber({
        routingNumber: routingNo,
        success: (res) => {
          const validateRespose = res && res.length > 0 ? res[0] : null;
          const validateStatus = validateRespose && validateRespose.status;
          const validateData = validateRespose && validateRespose.data;

          if (validateStatus && validateStatus === 'success') {
            setValues({ ...values, bankName: validateData.name, routingNumber: routingNo, isValidRoutingNumber: true });
            deleteErrors(['bankName', 'routingNumber']);
            setRoutingValidateStatus({ ...routingValidateStatus, checkedFirstTime: true, verified: true, loading: false, data: validateData });
          }
          if (validateStatus && validateStatus === 'fail') {
            setErrors({ ...errors, routingNumber: 'Routing Number is not valid' });
            setRoutingValidateStatus({ ...routingValidateStatus, checkedFirstTime: true, verified: false, loading: false, data: null });
          }
        },
        fail: (err) => {
          console.log('err', err);
          toast.error(
            (
              <div>
                <h5>An error occurred.</h5>
              </div>
            ),
            { autoClose: true }
          );
          setRoutingValidateStatus({ ...routingValidateStatus, checkedFirstTime: true, verified: false, loading: false, data: null });
        },
      });
    }
  };

  const handleAddDebitCard = () => {
    if (validate(debitCardSchema).isValid) {
      setIsAddingPayment(true);
      let data;
      if (currentPaymentMethod && currentPaymentMethod.paymentAccountId) {
        data = buildPutPayload(values, 'bankcard', currentPaymentMethod.paymentAccountId);
      } else {
        const formValues = {
          ...values,
          cardNumber: numberUnmask(values.cardNumber),
        };
        data = buildPostPayload(formValues, 'bankcard');
      }
      props.saveDebitCardToken(data, (isSucceed = false) => {
        if (isSucceed) {
          resetFormState();
        }
        setIsAddingPayment(false);
      });
    }
  };

  useEffect(() => {
    const isCard = currentPaymentMethod && currentPaymentMethod.type && typeof currentPaymentMethod.type === 'string' && ['debit', 'credit'].includes(currentPaymentMethod.type.toLowerCase());
    const newActiveTab = isCard ? 1 : 0;

    if (newActiveTab !== activeTab) setActiveTab(newActiveTab);

    if (currentPaymentMethod && !isCard) {
      setValues({
        accountType: currentPaymentMethod.type[2] || '',
        bankName: currentPaymentMethod.accountName || '',
        routingNumber: currentPaymentMethod.routingNumber || '',
        accountNumber: currentPaymentMethod.accountNumber || '',
        accountholderName: currentPaymentMethod.accountholderName || '',
        address: get(currentPaymentMethod, 'address.address') || '',
        city: get(currentPaymentMethod, 'address.city') || '',
        state: get(currentPaymentMethod, 'address.state') || '',
        zipcode: get(currentPaymentMethod, 'address.zipCode') || '',
        isValidRoutingNumber: true,
      });
    } else if (currentPaymentMethod && isCard) {
      setAllowExpirationDate(false);
      setValues({
        cardNumber: currentPaymentMethod.accountNumber ? `xxxx-xxxx-xxxx-${`${currentPaymentMethod.accountNumber}`.slice(-4)}` : '',
        cardHolderName: currentPaymentMethod.accountholderName ? currentPaymentMethod.accountholderName : '',
        address: get(currentPaymentMethod, 'address.address') || '',
        city: get(currentPaymentMethod, 'address.city') || '',
        state: get(currentPaymentMethod, 'address.state') || '',
        zipcode: get(currentPaymentMethod, 'address.zipCode') || '',
        expirationDate: currentPaymentMethod.cardExpiration ? currentPaymentMethod.cardExpiration : '',
      });
    }
  }, [props.currentPaymentMethod]);

  const resetExpDate = () => {
    setAllowExpirationDate(true);
    props.validator.onChangeHandler('expirationDate', '');
  };

  const handleActiveTab = (tab) => {
    if (tab !== activeTab) {
      props.validator.setValues({});
      setIsAccountNumberVisible(true);
      setSameAsProfileAddress(null);
      setActiveTab(tab);
    }
  };

  const syncConfirmAccountNumberError = (accountNumber, confirmAccountNumber) => {
    if (!confirmAccountNumber) {
      if (errors.confirmAccountNumber === 'Account numbers do not match') {
        deleteErrors(['confirmAccountNumber']);
      }
      return;
    }

    if (`${confirmAccountNumber}` !== `${accountNumber || ''}`) {
      setErrors({ confirmAccountNumber: 'Account numbers do not match' });
      return;
    }

    if (errors.confirmAccountNumber === 'Account numbers do not match') {
      deleteErrors(['confirmAccountNumber']);
    }
  };

  const handleInputChange = (event) => {
    const { validator: { onChangeHandler } } = props;
    const { name, value } = event.target;

    if (['address', 'city', 'state', 'zipcode'].includes(name) && sameAsProfileAddress) {
      setSameAsProfileAddress(false);
    }

    if (name === 'accountholderName' || name === 'cardHolderName') {
      onChangeHandler(name, value.replace(/[^a-zA-Z '-]/g, ''));
    } else if (name === 'routingNumber' && !routingValidateStatus.loading && validateRoutingNo(value) && value !== values.routingNumber) {
      validateRoutingNumber(value);
      onChangeHandler(name, value);
    } else if (name === 'accountNumber') {
      let nextValue;
      if (!isAccountNumberVisible) {
        // While masked, the input only shows asterisks. Any real digits the user
        // just typed are the non-asterisk characters; append them to the existing
        // real value instead of replacing it.
        const typedChars = `${value}`.replace(/\*/g, '');
        nextValue = `${values.accountNumber || ''}${typedChars}`;
        setIsAccountNumberVisible(true);
      } else {
        nextValue = value;
      }
      onChangeHandler(name, nextValue);
      syncConfirmAccountNumberError(nextValue, values.confirmAccountNumber);
    } else if (name === 'confirmAccountNumber') {
      onChangeHandler(name, value);
      syncConfirmAccountNumberError(values.accountNumber, value);
    } else if (name === 'expirationDate') {
      // Auto-insert a slash after the first 2 digits => MM/YYYY.
      const digits = `${value}`.replace(/\D/g, '').slice(0, 6);
      const formatted = digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
      onChangeHandler(name, formatted);
    } else {
      onChangeHandler(name, value);
    }
  };

  const handleConfirmAccountNumberBlur = () => {
    if (!values.confirmAccountNumber) return;
    syncConfirmAccountNumberError(values.accountNumber, values.confirmAccountNumber);
  };

  const handleAccountTypeChange = (accountType) => {
    if (paymentModalType !== 'add') return;
    const { validator: { onChangeHandler } } = props;
    onChangeHandler('accountType', accountType);
    deleteErrors(['accountType']);
  };

  const handleSameAsProfileAddressChange = (checked) => {
    setSameAsProfileAddress(checked);
    if (checked) {
      applyProfileAddress();
    } else {
      clearAddressFields();
    }
  };

  const handleAccountNumberBlur = () => {
    setIsAccountNumberVisible(false);
  };

  const handleAccountNumberVisibilityClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsAccountNumberVisible((visible) => !visible);
  };

  useEffect(() => {
    if (paymentModalType !== 'add' || !isAccountNumberVisible || !values.accountNumber) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (accountNumberFieldRef.current && !accountNumberFieldRef.current.contains(event.target)) {
        setIsAccountNumberVisible(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [paymentModalType, isAccountNumberVisible, values.accountNumber]);

  const handleAddPayment = () => {
    const schemaToValidate = paymentModalType === 'add' ? schema : editBankAccountSchema;

    if (validate(schemaToValidate).isValid) {
      setIsAddingPayment(true);

      let data;

      if (paymentModalType === 'save') {
        data = buildPutPayload(values, 'bankaccount', currentPaymentMethod.paymentAccountId);
      } else {
        data = buildPostPayload(values, 'bankaccount');
      }

      props.saveToken(data, (isSucceed = false) => {
        if (isSucceed) resetFormState();
        setIsAddingPayment(false);
      });
    } else {
      console.log('api error', errors);
    }
  };

  const toggleModal = () => {
    resetFormState();
    props.toggle(0);
  };

  const handleUseDifferentBankAccount = (event) => {
    event.preventDefault();
    resetFormState();
    if (props.onUseDifferentBankAccount) {
      props.onUseDifferentBankAccount();
    } else {
      props.toggle(0);
    }
  };

  const isAccountNumberMasked = paymentModalType === 'add' && !isAccountNumberVisible && !!values.accountNumber;
  const accountNumberDisplayValue = isAccountNumberMasked
    ? '*'.repeat(`${values.accountNumber}`.length)
    : (values.accountNumber || '');
  const profileAddress = getProfileAddress();
  const hasProfileAddress = !!(profileAddress && (profileAddress.address1 || profileAddress.address));
  const accountTypeLabel = `${get(currentPaymentMethod, 'type.2') || values.accountType || 'checking'}`.replace(/^\w/, (c) => c.toUpperCase());
  const bankDisplayName = get(currentPaymentMethod, 'accountName') || values.bankName || 'Bank Account';
  const accountLast4 = `${get(currentPaymentMethod, 'accountNumber') || values.accountNumber || ''}`.slice(-4);
  const routingDisplay = get(currentPaymentMethod, 'routingNumber') || values.routingNumber || '';
  // Disabled until routing lookup fills bank name; then editable for corrections
  const isBankNameEnabled = paymentModalType === 'add'
    && routingValidateStatus.verified
    && !routingValidateStatus.loading;
  const isAddBankFormComplete = !!(
    values.routingNumber
    && values.isValidRoutingNumber
    && values.bankName
    && values.accountNumber
    && values.confirmAccountNumber
    && `${values.confirmAccountNumber}` === `${values.accountNumber}`
    && values.accountType
    && values.accountholderName
    && values.address
    && values.city
    && values.state
    && values.zipcode
  );
  const debitCardNumberDigits = numberUnmask(get(values, 'cardNumber', ''));
  const isDebitCardNumberComplete = !!(
    get(values, 'cardNumber')
    && (
      `${get(values, 'cardNumber')}`.includes('X')
      || (
        debitCardNumberDigits.length >= 16
        && (debitCardNumberDigits.startsWith('4')
          || debitCardNumberDigits.startsWith('5')
          || debitCardNumberDigits.startsWith('6')
        )
      )
    )
  );
  const isAddDebitFormComplete = !!(
    values.cardHolderName
    && removeSpace(values.cardHolderName).length > 0
    && `${values.cardHolderName}`.trim().split(/\s+/).length >= 2
    && isDebitCardNumberComplete
    && values.expirationDate
    && (
      `${values.expirationDate}`.includes('X')
      || numberUnmask(values.expirationDate).length >= 6
    )
    && values.address
    && removeSpace(values.address).length > 0
    && values.city
    && removeSpace(values.city).length > 0
    && values.state
    && removeSpace(values.state).length > 0
    && values.zipcode
    && removeUnderline(values.zipcode).length === 5
  );

  const renderEditBankAccount = () => (
    <Container fluid className="pl-3 pr-3 add-payment-form">
      <div className="locked-bank-summary">
        <div className="locked-bank-summary__icon" aria-hidden="true">
          {getBankInitials(bankDisplayName)}
        </div>
        <div className="locked-bank-summary__content">
          <p className="locked-bank-summary__name">{bankDisplayName}</p>
          <p className="locked-bank-summary__meta">
            {accountTypeLabel}
            {accountLast4 ? ` · account number: xxxxxx${accountLast4}` : ''}
            {routingDisplay ? ` · routing: ${routingDisplay}` : ''}
          </p>
        </div>
      </div>

      <p className="locked-bank-note">
        Bank details are locked for your security.
        {' '}
        <button type="button" onClick={handleUseDifferentBankAccount}>
          Use a different bank account
        </button>
      </p>

      <Row>
        <Col xs={12}>
          <Input
            name="accountholderName"
            label="Account Holder Name"
            value={values.accountholderName}
            onChange={handleInputChange}
            placeHolder="Account Holder Name"
            isRequired
            hasError={!!errors.accountholderName}
            errorMessage={errors.accountholderName}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <Input
            name="address"
            label="Billing Address"
            value={values.address}
            onChange={handleInputChange}
            placeHolder="Billing Address"
            isRequired
            hasError={!!errors.address}
            errorMessage={errors.address}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} sm={5}>
          <Input
            name="city"
            label="City"
            value={values.city}
            onChange={handleInputChange}
            placeHolder="City"
            isRequired
            hasError={!!errors.city}
            errorMessage={errors.city}
          />
        </Col>
        <Col xs={12} sm={4}>
          <Select
            name="state"
            data={states}
            value={values.state}
            onChange={handleInputChange}
            label="State"
            isRequired
            hasError={!!errors.state}
            errorMessage={errors.state}
          />
        </Col>
        <Col xs={12} sm={3}>
          <Input
            name="zipcode"
            label="ZIP"
            isMasked={[/\d/, /\d/, /\d/, /\d/, /\d/]}
            value={values.zipcode}
            onChange={handleInputChange}
            placeHolder="ZIP"
            isRequired
            hasError={!!errors.zipcode}
            errorMessage={errors.zipcode}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Button
            color="primary"
            className="w-100 mt-1 mb-2"
            onClick={handleAddPayment}
            isLoading={isAddingPayment}
          >
            Save Changes
          </Button>
        </Col>
      </Row>
    </Container>
  );

  const renderBankAccount = () => {
    if (paymentModalType === 'save') {
      return renderEditBankAccount();
    }

    return (
      <Container fluid className="pl-3 pr-3 add-payment-form">
        <Row>
          <Col xs={12} sm={6}>
            <div className="position-relative routing-number-field">
              <Input
                name="routingNumber"
                label="Routing Number"
                value={values.routingNumber}
                isMasked={[/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]}
                onChange={handleInputChange}
                placeHolder="9 digits"
                isRequired
                hasError={!!errors.routingNumber}
                errorMessage={errors.routingNumber}
                isDisabled={paymentModalType !== 'add'}
                notification={ROUTING_NUMBER_HELP}
              />
              {routingValidateStatus.loading
                ? <span className="input-loader" />
                : routingValidateStatus.checkedFirstTime
                  ? (
                    <span className="input-status">
                      <img
                        src={`${routingValidateStatus.verified ? VerifiedIcon : WarningIcon}`}
                        alt="input-status"
                      />
                    </span>
                  )
                  : null}
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <div className="bank-name-field">
              {isBankNameEnabled ? (
                <p className="bank-name-hint">Confirm this is your bank</p>
              ) : null}
              <Input
                name="bankName"
                label="Bank Name"
                value={values.bankName}
                onChange={handleInputChange}
                placeHolder={isBankNameEnabled ? 'Bank Name' : 'Enter routing number first'}
                isRequired
                hasError={!!errors.bankName}
                errorMessage={errors.bankName}
                isDisabled={!isBankNameEnabled}
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col xs={12} sm={6}>
            <div className="position-relative account-number-field" ref={accountNumberFieldRef}>
              <Input
                name="accountNumber"
                label="Account Number"
                className={paymentModalType === 'add' && values.accountNumber ? 'has-visibility-toggle' : ''}
                value={accountNumberDisplayValue}
                onChange={handleInputChange}
                onBlur={handleAccountNumberBlur}
                placeHolder="Account number"
                isMasked={isAccountNumberMasked ? false : numberMask}
                isRequired
                hasError={!!errors.accountNumber}
                errorMessage={errors.accountNumber}
                isDisabled={paymentModalType !== 'add'}
                autoComplete="off"
              />
              {paymentModalType === 'add' && values.accountNumber ? (
                <button
                  type="button"
                  className="account-number-visibility-toggle"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onClick={handleAccountNumberVisibilityClick}
                >
                  {isAccountNumberVisible ? 'Hide' : 'Show'}
                </button>
              ) : null}
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <Input
              name="confirmAccountNumber"
              label="Confirm Account Number"
              value={values.confirmAccountNumber}
              onChange={handleInputChange}
              onBlur={handleConfirmAccountNumberBlur}
              placeHolder="Re-enter account number"
              isMasked={numberMask}
              isRequired
              hasError={!!errors.confirmAccountNumber}
              errorMessage={errors.confirmAccountNumber}
              autoComplete="off"
            />
          </Col>
        </Row>
        <Row className="account-type-holder-row">
          <Col xs={12} sm={6}>
            <div className={`account-type-field${errors.accountType ? ' required' : ''}`}>
              <span className="account-type-field__label">
                Account Type
                <em>Required</em>
              </span>
              <div className="account-type-field__options" role="radiogroup" aria-label="Account Type">
                {['checking', 'savings'].map((type) => {
                  const isSelected = values.accountType === type;
                  return (
                    <button
                      key={type}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      className={`account-type-field__option${isSelected ? ' account-type-field__option--selected' : ''}${paymentModalType !== 'add' ? ' account-type-field__option--disabled' : ''}`}
                      onClick={() => handleAccountTypeChange(type)}
                      disabled={paymentModalType !== 'add'}
                    >
                      <span className={`account-type-field__radio${isSelected ? ' account-type-field__radio--selected' : ''}`} />
                      {type === 'checking' ? 'Checking' : 'Savings'}
                    </button>
                  );
                })}
              </div>
              {!!errors.accountType && errors.accountType !== 'The input field is required' && (
                <div className="account-type-field__error">{errors.accountType}</div>
              )}
            </div>
          </Col>
          <Col xs={12} sm={6}>
            <Input
              name="accountholderName"
              label="Account Holder Name"
              value={values.accountholderName}
              onChange={handleInputChange}
              placeHolder="Name exactly as it appears on the account"
              isRequired
              hasError={!!errors.accountholderName}
              errorMessage={errors.accountholderName}
            />
          </Col>
        </Row>
        {hasProfileAddress && (
          <Row className="same-as-profile-row">
            <Col xs={12}>
              <button
                type="button"
                className="use-profile-address-link"
                onClick={() => handleSameAsProfileAddressChange(true)}
              >
                Use my profile address
              </button>
            </Col>
          </Row>
        )}
        <Row>
          <Col xs={12}>
            <Input
              name="address"
              label="Address"
              value={values.address}
              onChange={handleInputChange}
              placeHolder="Address"
              isRequired
              hasError={!!errors.address}
              errorMessage={errors.address}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} sm={5}>
            <Input
              name="city"
              label="City"
              value={values.city}
              onChange={handleInputChange}
              placeHolder="City"
              isRequired
              hasError={!!errors.city}
              errorMessage={errors.city}
            />
          </Col>
          <Col xs={12} sm={4}>
            <Select
              name="state"
              data={states}
              value={values.state}
              onChange={handleInputChange}
              label="State"
              isRequired
              hasError={!!errors.state}
              errorMessage={errors.state}
            />
          </Col>
          <Col xs={12} sm={3}>
            <Input
              name="zipcode"
              label="ZIP"
              isMasked={[/\d/, /\d/, /\d/, /\d/, /\d/]}
              value={values.zipcode}
              onChange={handleInputChange}
              placeHolder="ZIP"
              isRequired
              hasError={!!errors.zipcode}
              errorMessage={errors.zipcode}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Button
              color="primary"
              className="w-100 mt-1 mb-2"
              onClick={handleAddPayment}
              isLoading={isAddingPayment}
              isDisabled={!isAddBankFormComplete}
            >
              Add new payment
            </Button>
          </Col>
        </Row>
      </Container>
    );
  };

  const isCardExisting = paymentModalType === 'save';
  const cardLast4 = `${get(values, 'cardNumber', '')}`.replace(/[^0-9]/g, '').slice(-4);

  const handleCardChange = (digits) => {
    const { validator: { onChangeHandler } } = props;
    onChangeHandler('cardNumber', digits);
  };

  const handleCardComplete = (digits, isCardValid) => {
    if (!isCardValid) {
      setErrors({ ...errors, cardNumber: 'Invalid card number' });
    } else {
      deleteErrors(['cardNumber']);
    }
  };

  const handleCardBlur = (digits, isCardValid) => {
    if (digits && !isCardValid) {
      setErrors({ ...errors, cardNumber: 'Invalid card number' });
    }
  };

  const handleCardBrandChange = (cardBrand, isAllowed) => {
    if (cardBrand && !isAllowed) {
      setCardError('Currently we accept only Discover, Visa or Mastercard');
    } else {
      setCardError('');
    }
  };

  const renderDebtCard = () => (
    <Container fluid className="pl-3 pr-3 add-payment-form">
      <Row>
        <Col sm={6}>
          <Row>
            <Col>
              <Input
                name="cardHolderName"
                label="Cardholder Name"
                value={get(values, 'cardHolderName') || ''}
                onChange={handleInputChange}
                placeHolder="Cardholder Name"
                isRequired
                hasError={!!get(errors, 'cardHolderName')}
                errorMessage={get(errors, 'cardHolderName')}
              />
            </Col>
          </Row>
          <Row>
            <Col className="payment-logo">
              <CardNumberInput
                className="card-number-input"
                value={isCardExisting ? null : (get(values, 'cardNumber') || null)}
                lastFour={isCardExisting ? (cardLast4 || null) : null}
                allowedBrands={['visa', 'mastercard', 'discover']}
                onChange={handleCardChange}
                onComplete={handleCardComplete}
                onBlur={handleCardBlur}
                onBrandChange={handleCardBrandChange}
                disabled={isCardExisting}
                error={(!!get(errors, 'cardNumber') && get(errors, 'cardNumber') !== 'The input field is required') || !!cardError}
                label="Card Number"
                placeholder="Card Number"
                isRequired
              />
              {((get(errors, 'cardNumber') && get(errors, 'cardNumber') !== 'The input field is required') || cardError) ? (
                <span className="error">
                  {(get(errors, 'cardNumber') !== 'The input field is required' && get(errors, 'cardNumber')) || cardError}
                </span>
              ) : null}
            </Col>
          </Row>
          <Row>
            <Col>
              <Input
                name="expirationDate"
                label="Expiration Date"
                onChange={handleInputChange}
                value={get(values, 'expirationDate') || ''}
                onClick={paymentModalType === 'save' && !allowExpirationDate ? resetExpDate : null}
                isMasked={false}
                isRequired
                hasError={!!get(errors, 'expirationDate')}
                errorMessage={get(errors, 'expirationDate')}
                placeHolder="MM/YYYY"
              />
            </Col>
          </Row>
        </Col>
        <Col sm={6} className="debit-address-col">
          {hasProfileAddress && paymentModalType === 'add' && (
            <button
              type="button"
              className="use-profile-address-link debit-use-profile-address-link"
              onClick={() => handleSameAsProfileAddressChange(true)}
            >
              Use my profile address
            </button>
          )}
          <Row>
            <Col>
              <Input
                name="address"
                label="Address"
                value={get(values, 'address') || ''}
                onChange={handleInputChange}
                placeHolder="Address"
                isRequired
                hasError={!!get(errors, 'address')}
                errorMessage={get(errors, 'address')}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <Input
                name="city"
                label="City"
                value={get(values, 'city') || ''}
                onChange={handleInputChange}
                placeHolder="City"
                isRequired
                hasError={!!get(errors, 'city')}
                errorMessage={get(errors, 'city')}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={7}>
              <Select
                name="state"
                data={states}
                value={get(values, 'state') || ''}
                onChange={handleInputChange}
                label="State"
                isRequired
                hasError={!!get(errors, 'state')}
                errorMessage={get(errors, 'state')}
              />
            </Col>
            <Col sm={5}>
              <Input
                name="zipcode"
                label="Zip"
                isMasked={[/\d/, /\d/, /\d/, /\d/, /\d/]}
                value={get(values, 'zipcode') || ''}
                onChange={handleInputChange}
                placeHolder="Zip"
                isRequired
                hasError={!!get(errors, 'zipcode')}
                errorMessage={get(errors, 'zipcode')}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col>
          <Button
            color="primary"
            className="w-100 mt-1 mb-2"
            onClick={handleAddDebitCard}
            isLoading={isAddingPayment}
            isDisabled={!isAddDebitFormComplete}
          >
            {paymentModalType === 'add' ? 'Add new payment' : 'Edit payment'}
          </Button>
        </Col>
      </Row>
    </Container>
  );
  const renderTabContent = () => (activeTab === 0 ? renderBankAccount() : renderDebtCard());
  const showPaymentTypeTabs = paymentModalType === 'add';

  return (
    <Modal isOpen={props.isOpen} toggle={toggleModal} size="lg" style={{ maxWidth: 760 }}>
      <ModalHeader className="no-border" toggle={toggleModal}>
        {paymentModalType === 'add' ? 'Add A Payment Method' : 'Edit Payment Method'}
        <button type="button" className="close" onClick={toggleModal} aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </ModalHeader>
      {showPaymentTypeTabs && (
        <ButtonGroup size="sm" className="d-flex ml-3 mr-3 mb-3">
          <Button
            color="primary"
            onClick={handleActiveTab.bind(null, 0)}
            active={activeTab === 0}
            style={{ maxWidth: '50%' }}
          >
            Bank Account
          </Button>
          <Button
            color="primary"
            onClick={handleActiveTab.bind(null, 1)}
            active={activeTab === 1}
          >
            Debit Card
          </Button>
        </ButtonGroup>
      )}
      {renderTabContent()}
    </Modal>
  );
};

AddPaymentMethods.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  validator: PropTypes.object.isRequired,
  paymentModalType: PropTypes.string.isRequired,
  currentPaymentMethod: PropTypes.object,
  saveToken: PropTypes.func.isRequired,
  saveDebitCardToken: PropTypes.func.isRequired,
  validateBankRoutingNumber: PropTypes.func.isRequired,
  onUseDifferentBankAccount: PropTypes.func,
};

AddPaymentMethods.defaultProps = {
  currentPaymentMethod: null,
  onUseDifferentBankAccount: null,
};

export default Validator(schema)(AddPaymentMethods);
